import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useGetTeamPlaybooks } from '@dc-shared-web/hooks/api/cl-cs/useGetTeamPlaybooks'
import {
  PlaybookListResponse,
  PlaybookListItem,
} from '@dc-shared-web/hooks/api/cl-cs/types'
import { useLDFlags } from '@/app/core/hooks/useLDFlags'

/**
 * Optimized hook for fetching team playbooks that only fetches data for new crunch UUIDs
 * and caches previously fetched data locally to avoid unnecessary API calls.
 * Only fetches data when the pd2UploadFlow10 feature flag is enabled.
 */
export const useOptimizedTeamPlaybooks = (crunchUuids: string[]) => {
  const { flags } = useLDFlags()
  // Store all playbook data locally
  const [cachedPlaybookData, setCachedPlaybookData] = useState<
    PlaybookListItem[]
  >([])
  // Track which UUIDs we've already fetched
  const fetchedUuidsRef = useRef<Set<string>>(new Set())
  // Track if we're currently loading initial data
  const [isInitialLoading, setIsInitialLoading] = useState(true)

  // Find new UUIDs that we haven't fetched yet
  const newUuids = useMemo(() => {
    return crunchUuids.filter((uuid) => !fetchedUuidsRef.current.has(uuid))
  }, [crunchUuids])

  // Only make API call if there are new UUIDs to fetch AND the feature flag is enabled
  const shouldFetch = newUuids.length > 0 && flags.pd2UploadFlow10
  const newUuidsString = newUuids.join(',')

  const {
    data: newPlaybookData,
    isLoading: isFetchingNew,
    error,
  } = useGetTeamPlaybooks({
    crunchUuids: shouldFetch ? newUuidsString : '',
    page: 1,
  })

  // Merge new data with cached data when new data arrives
  useEffect(() => {
    if (newPlaybookData?.results && newUuids.length > 0) {
      setCachedPlaybookData((prev) => {
        // Create a map for efficient lookup of existing items
        const existingMap = new Map(
          prev.map((item) => [item.crunch_uuid, item])
        )

        // Add new items, avoiding duplicates
        newPlaybookData.results.forEach((newItem) => {
          existingMap.set(newItem.crunch_uuid, newItem)
        })

        return Array.from(existingMap.values())
      })

      // Mark these UUIDs as fetched
      newUuids.forEach((uuid) => {
        fetchedUuidsRef.current.add(uuid)
      })
    }
  }, [newPlaybookData, newUuids])

  // Handle initial loading state
  useEffect(() => {
    if (isInitialLoading && crunchUuids.length > 0 && !isFetchingNew) {
      setIsInitialLoading(false)
    }
  }, [crunchUuids.length, isFetchingNew, isInitialLoading])

  // Clear cache when crunchUuids is empty (e.g., new search or filter)
  useEffect(() => {
    if (crunchUuids.length === 0) {
      setCachedPlaybookData([])
      fetchedUuidsRef.current.clear()
      setIsInitialLoading(true)
    }
  }, [crunchUuids.length])

  // Create a response object that matches the original hook's return type
  const optimizedData: PlaybookListResponse | undefined = useMemo(() => {
    // Return undefined if feature flag is disabled
    if (!flags.pd2UploadFlow10) {
      return undefined
    }

    if (cachedPlaybookData.length === 0 && crunchUuids.length > 0) {
      return undefined
    }

    if (cachedPlaybookData.length === 0 && crunchUuids.length === 0) {
      return undefined
    }

    return {
      results: cachedPlaybookData,
      total: cachedPlaybookData.length,
      limit: 100,
      page: 1,
      totalpages: Math.ceil(cachedPlaybookData.length / 100),
    }
  }, [cachedPlaybookData, crunchUuids.length, flags.pd2UploadFlow10])

  const isLoading =
    flags.pd2UploadFlow10 &&
    ((isInitialLoading && crunchUuids.length > 0) || isFetchingNew)

  return {
    data: optimizedData,
    isLoading,
    error,
    // Expose cache management functions for testing or manual control
    clearCache: useCallback(() => {
      setCachedPlaybookData([])
      fetchedUuidsRef.current.clear()
      setIsInitialLoading(true)
    }, []),
    getCachedCount: useCallback(
      () => cachedPlaybookData.length,
      [cachedPlaybookData.length]
    ),
    getFetchedUuidsCount: useCallback(() => fetchedUuidsRef.current.size, []),
  }
}
